import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePreferenceStore } from '@/lib/store/preferenceStore';
import Card from '@/components/common/Card';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import { supabase } from '@/lib/supabase/client';

export const PreferenceStatusCard = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { teamPreferences, isLoading, fetchTeamPreferences } = usePreferenceStore();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [roleLoading, setRoleLoading] = useState<boolean>(true);
  
  useEffect(() => {
    if (teamId) {
      fetchTeamPreferences(teamId);
    }
  }, [teamId, fetchTeamPreferences]);
  
  // Check if the user is an admin
  useEffect(() => {
    const checkUserRole = async () => {
      if (!teamId || !supabase) return;
      
      try {
        setRoleLoading(true);
        
        // Get current user
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user) {
          setIsAdmin(false);
          setRoleLoading(false);
          return;
        }
        
        // Check if user is an admin
        const { data: member, error: memberError } = await supabase
          .from('team_members')
          .select('role')
          .eq('team_id', teamId)
          .eq('user_id', userData.user.id)
          .single();
        
        if (memberError) {
          console.error('Error checking team role:', memberError);
          setIsAdmin(false);
        } else {
          setIsAdmin(member?.role === 'admin');
        }
      } catch (err) {
        console.error('Error checking team role:', err);
        setIsAdmin(false);
      } finally {
        setRoleLoading(false);
      }
    };
    
    checkUserRole();
  }, [teamId]);
  
  if (isLoading || roleLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-3">Preference Settings</h2>
        <div className="flex justify-center py-4">
          <div className="animate-spin h-6 w-6 border-t-2 border-b-2 border-primary rounded-full"></div>
        </div>
      </Card>
    );
  }
  
  if (!teamPreferences) {
    return (
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-3">Preference Settings</h2>
        <p className="text-gray-500 mb-4">Team preferences not configured yet.</p>
        {isAdmin && (
          <Link to={`/teams/${teamId}/preferences`}>
            <Button variant="primary" size="sm">Configure Preferences</Button>
          </Link>
        )}
      </Card>
    );
  }
  
  // Extract important preferences for display
  const { 
    provider_preference_deadline_days, 
    auto_publish_schedule,
    weekend_distribution_strategy
  } = teamPreferences.scheduling_preferences;
  
  // Format weekend strategy for display
  const formatWeekendStrategy = (strategy: string) => {
    switch (strategy) {
      case 'equal':
        return 'Equal Distribution';
      case 'preference_based':
        return 'Preference-Based';
      case 'seniority_based':
        return 'Seniority-Based';
      default:
        return strategy;
    }
  };
  
  return (
    <Card className="p-6">
      <div className="flex justify-between items-start mb-3">
        <h2 className="text-lg font-semibold">Preference Settings</h2>
        {isAdmin && (
          <Link to={`/teams/${teamId}/preferences`}>
            <Button variant="text" size="sm">Edit</Button>
          </Link>
        )}
      </div>
      
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-gray-500">Auto-Publish Schedule:</span>
          <Badge variant={auto_publish_schedule ? 'success' : 'secondary'}>
            {auto_publish_schedule ? 'Enabled' : 'Disabled'}
          </Badge>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-gray-500">Preference Deadline:</span>
          <span className="font-medium">{provider_preference_deadline_days} days before</span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-gray-500">Weekend Distribution:</span>
          <span className="font-medium">{formatWeekendStrategy(weekend_distribution_strategy)}</span>
        </div>
      </div>
    </Card>
  );
}; 